/* eslint-disable no-param-reassign */
import { types, applySnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import { toJS } from 'mobx';
import * as constants from '../../../../common/constants';
import utils from '../../../../common/utils';
import { buildRowScopedExportPayload } from '../../common/utils/auditExportColumnUtils';

// Individual feedback log item model
const FeedbackLogItem = types.model('FeedbackLogItem', {
  requestId: types.maybeNull(types.string),
  feedbackId: types.maybeNull(types.string),
  feedback: types.maybeNull(types.string), // LIKE, DISLIKE
  serverDateTime: types.maybeNull(types.string),
  createdDate: types.maybeNull(types.number),
  identifierId: types.maybeNull(types.string),
  graphId: types.maybeNull(types.string),
  graphVersion: types.maybeNull(types.string),
  userId: types.maybeNull(types.string),
  type: types.maybeNull(types.string), // discuss, image_explain, etc.
  content: types.maybeNull(types.frozen()), // Simplified - no need for nested model
  comments: types.maybeNull(types.string),
  responseId: types.maybeNull(types.string),
  reasonForDislike: types.optional(types.frozen(), null),
  fromAgentic: types.optional(types.boolean, false),
  displayType: types.maybeNull(types.string) // Explain, Image Explain, etc.
});

// Main FeedbackLogsModel
const FeedbackLogsModel = types
  .model('FeedbackLogsModel', {
    data: types.optional(types.array(FeedbackLogItem), []),
    totalCount: types.optional(types.number, 0),
    totalPages: types.optional(types.number, 0),
    currentPage: types.optional(types.number, 0),
    pageSize: types.optional(types.number, constants.PAGINATION.LIMIT),
    lastPayload: types.optional(types.frozen(), {}),
    /** Date range from the last Search; kept intact because pagination narrows `lastPayload.endDate`. */
    searchDateRange: types.optional(types.frozen(), {}),
    /** True when last Search used both startDate and endDate (pagination may echo server endDate). */
    paginationUsesDateRange: types.optional(types.boolean, false),
    /** Latest API `searchedParams` (payload echo); used for endDate on next/prev only. */
    lastSearchedParams: types.optional(types.maybeNull(types.frozen()), null)
  })
  .views((self) => ({
    get feedbackLogs() {
      return toJS(self.data);
    },
    get hasData() {
      return self.data.length > 0;
    },
    get paginationInfo() {
      return {
        totalCount: self.totalCount,
        totalPages: self.totalPages,
        currentPage: self.currentPage,
        pageSize: self.pageSize
      };
    },
    /** Export filters; start/end dates come from loaded row timestamps, not search date pickers. */
    get exportPayload() {
      return buildRowScopedExportPayload(self.lastPayload, self.data);
    }
  }))
  .actions((self) => ({
    setPageSize(size) {
      self.pageSize = size;
    },
    setLastPayload(payload) {
      self.lastPayload = payload;
    },
    setSearchDateRange(range) {
      self.searchDateRange = range;
    },
    setTotalCount(count) {
      self.totalCount = count;
    },
    setTotalPages(pages) {
      self.totalPages = pages;
    },
    setCurrentPage(page) {
      self.currentPage = page;
    },
    fetchInitial(payload = {}) {
      self.lastSearchedParams = null;
      self.resetData();
      self.paginationUsesDateRange = Boolean(payload.startDate && payload.endDate);

      const requestPayload = {
        ...payload,
        offset: 0,
        limit: self.pageSize
      };
      const searchDateRange = {};
      if (payload.startDate != null && payload.startDate !== '') {
        requestPayload.startDate = payload.startDate;
        searchDateRange.startDate = payload.startDate;
      }
      if (payload.endDate != null && payload.endDate !== '') {
        requestPayload.endDate = payload.endDate;
        searchDateRange.endDate = payload.endDate;
      }

      self.setSearchDateRange(searchDateRange);
      self.setLastPayload(requestPayload);

      Framework.getEventManager().publish(constants.GET_FEEDBACK_DATA, {
        payload: requestPayload
      });
    },
    fetchPage(page, limit = null) {
      const pageSize = limit || self.pageSize;
      const offset = page * pageSize;

      let requestPayload = {
        ...self.lastPayload,
        offset,
        limit: pageSize
      };

      const serverEndDate = self.lastSearchedParams?.endDate;
      if (self.paginationUsesDateRange && serverEndDate != null && serverEndDate !== '') {
        requestPayload = {
          ...requestPayload,
          endDate: serverEndDate
        };
      }

      self.setPageSize(pageSize);
      self.setLastPayload(requestPayload);

      Framework.getEventManager().publish(constants.GET_FEEDBACK_DATA, {
        payload: requestPayload
      });
    },
    setResponse(response) {
      if (!response) {
        Framework.getEventManager().publish(constants.GET_FEEDBACK_RESPONSE);
        return;
      }

      // Extract response data and pagination
      let responseData = [];
      let paginationData = null;
      let searchedParams = null;

      if (response.success !== undefined) {
        responseData = response.data || [];
        paginationData = response.pagination || null;
        searchedParams = response.searchedParams ?? paginationData?.searchedParams ?? null;
      } else if (Array.isArray(response)) {
        responseData = response;
      } else if (response.data) {
        responseData = response.data;
        paginationData = response.pagination || null;
        searchedParams = response.searchedParams ?? paginationData?.searchedParams ?? null;
      }

      const sanitizedData = responseData.map((item) => ({
        ...item,
        content: item.content || null,
        createdDate: utils.toEpochUTC(item.serverDateTime)
      }));

      // Update data - always replace (no append for pagination)
      applySnapshot(self.data, sanitizedData);

      if (searchedParams && typeof searchedParams === 'object' && !Array.isArray(searchedParams)) {
        self.lastSearchedParams = { ...searchedParams };
      }

      // Update pagination info from server response
      if (paginationData) {
        self.setTotalCount(paginationData.totalCount || 0);
        self.setTotalPages(paginationData.totalPages || 0);

        // Calculate 0-based page index from offset and limit (MaterialUI expects 0-based)
        // This handles both 0-based and 1-based server responses correctly
        const currentPageIndex =
          paginationData.offset !== undefined && paginationData.limit
            ? Math.floor(paginationData.offset / paginationData.limit)
            : 0;
        self.setCurrentPage(currentPageIndex);
      } else {
        // Fallback if no pagination data
        self.setTotalCount(sanitizedData.length);
        self.setTotalPages(1);
        self.setCurrentPage(0);
      }

      // Publish response event
      Framework.getEventManager().publish(constants.GET_FEEDBACK_RESPONSE);
    },
    resetData() {
      applySnapshot(self.data, []);
      self.setTotalCount(0);
      self.setTotalPages(0);
      self.setCurrentPage(0);
      self.setLastPayload({});
      self.setSearchDateRange({});
      self.paginationUsesDateRange = false;
      self.lastSearchedParams = null;
    },
    resetStoreValues() {
      // Legacy method for compatibility
      self.resetData();
      Framework.getEventManager().publish(constants.GET_FEEDBACK_RESPONSE);
    }
  }));

export default FeedbackLogsModel;
